import { ReactNode, Suspense, useDeferredValue, useRef, useState } from 'react';
import * as styles from './SupplierPicker.css';
import { Box, Dialog, DialogContent, Spinner } from '@ff-it/ui';
import type { Supplier } from '../../types';
import type { Activity, ActivityType, EmbeddedCompany, ProductKind } from 'types';
import { FilterContainer, useFilterState, useKindOptions, type FilterState } from './Filter';
import { ProductChainPicker } from '../ProductChainPicker';
import { includeSelectedProduct, useSupplierProductChain } from './productChainAdapter';
import { Form } from '@ff-it/form';
import { EmbeddedProduct } from 'modules/supplier/products';
import { SupplierForm } from './SupplierForm';
import { SubmissionErrors } from '@ff-it/form';
import eq from 'fast-deep-equal';

type SupplierFormProps = {
  value: Supplier | null;
  onChange: (value: Supplier | null) => Promise<SubmissionErrors | void> | SubmissionErrors | void;
  onRequestClose: () => void;
  required?: boolean;
  partial?: boolean;
};

export type SupplierPickerProps = SupplierFormProps & {
  type: ActivityType;
  kind?: ProductKind | ProductKind[];
  outlet_or_service_category?: number;
  for_block?: number;
};

export function SupplierPicker({
  value,
  onChange,
  type,
  kind,
  onRequestClose,
  required,
  partial,
  outlet_or_service_category,
  for_block,
}: SupplierPickerProps): ReactNode {
  const searchInputRef = useRef<HTMLInputElement>(null);
  // Reopening restores the saved product's kind as the active kind when
  // permitted, so SERVICE/SMART suppliers do not open in MEDIA results.
  const [filter, setFilter] = useFilterState(type, kind, value?.product?.kind);
  const initialFilter = useRef(filter).current;
  const deferredFilter = useDeferredValue(filter);
  const kindOptions = useKindOptions(kind);
  const [product, setProduct] = useState<EmbeddedProduct | null>(value?.product || null);

  const handleSubmit = (values: { activity: Activity; provider: EmbeddedCompany }) => {
    // if product present hast to be complete
    if (product) {
      onChange({ product, ...values });
    } else {
      // @ts-ignore
      onChange(partial ? { product, ...values } : null);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onRequestClose}>
      <DialogContent testId="supplier-picker" className={styles.dialog} initialFocus={searchInputRef} p="4">
        <Form
          autoComplete="off"
          onSubmit={handleSubmit}
          className={styles.container}
          initialValues={{
            activity: value?.activity || null,
            provider: value?.provider || null,
          }}
        >
          <FilterContainer
            filter={filter}
            setFilter={setFilter}
            kindOptions={kindOptions}
            searchInputRef={searchInputRef}
          />
          <Suspense
            fallback={
              <Box display="flex" flexGrow={1} alignItems="center" justifyContent="center" p="3">
                <Spinner />
              </Box>
            }
          >
            <div className={styles.columns}>
              <ProductCatalogue
                filter={deferredFilter}
                product={product}
                selectProduct={setProduct}
                outlet_or_service_category={outlet_or_service_category}
                restoreProductPath={eq(deferredFilter, initialFilter)}
              />
              <SupplierForm
                product={product}
                providerId={filter.provider?.id}
                setProduct={setProduct}
                required={required}
                partial={partial}
                for_block={for_block}
              />
            </div>
          </Suspense>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

type ProductCatalogueProps = {
  filter: FilterState;
  product: EmbeddedProduct | null;
  selectProduct: (product: EmbeddedProduct | null) => void;
  outlet_or_service_category?: number;
  restoreProductPath: boolean;
};

/**
 * Left column of the supplier dialog: catalogue navigation and product
 * selection through the shared chain picker. Suspends on the product query.
 */
function ProductCatalogue({
  filter,
  product,
  selectProduct,
  outlet_or_service_category,
  restoreProductPath,
}: ProductCatalogueProps): ReactNode {
  const { productCount, isTruncated, ...discoveredCatalogue } = useSupplierProductChain(
    filter,
    outlet_or_service_category,
  );
  const discoveredCount = discoveredCatalogue.paths.length;
  const { paths, productsById } = restoreProductPath
    ? includeSelectedProduct(discoveredCatalogue, product)
    : discoveredCatalogue;
  const value = product ? { kind: 'product' as const, id: product.id } : null;

  return (
    <div className={styles.catalogue}>
      <ProductChainPicker
        className={styles.chain}
        value={value}
        paths={paths}
        selectableKinds={['product']}
        onChange={(target) => selectProduct(target ? (productsById.get(target.id) ?? null) : null)}
      />
      {isTruncated && (
        <Box color="warning700" textSize="xs" p="1.5">
          Showing the first {discoveredCount} of {productCount} products. Refine your search to see other results.
        </Box>
      )}
    </div>
  );
}
